import * as z from 'zod';

import { individualPaymentParcelSaveViewModelSchema } from '@/shared/api/generated';

/**
 * Campos do `IndividualPaymentParcelSaveViewModel` que **algum** formulário de
 * parcela controla — a união do que a criação e a edição precisam.
 *
 * Derivado com `.pick()` do schema gerado pelo Kubb, nunca reescrito à mão.
 *
 * Os dois formulários compartilham um único shape de propósito: os organisms de
 * seção são os mesmos nas duas telas, e `Control<A>` não é atribuível a
 * `Control<B>` quando `A` e `B` diferem. Um shape só evita generificar cinco
 * componentes para acomodar dois campos a mais.
 *
 * Cada tela decide o que **renderiza** e o que **envia**: a criação ignora os
 * campos de nota fiscal e de flag; a edição ignora `multiple` e `amountOfParcel`.
 *
 * Fora da união: `id` e `courseFinancialPersonId` vêm da rota, e `statusId` não é
 * editável em nenhuma das duas telas — não existe lookup de status de parcela, e
 * na criação quem define o status inicial é a máquina de estados do servidor.
 */
const editableShape = individualPaymentParcelSaveViewModelSchema.pick({
  typeId: true,
  numberParcel: true,
  total: true,
  paymentMethod: true,
  discountPunctualPercentage: true,
  retroactiveDiscount: true,
  totalDiscount: true,
  fine: true,
  expirationDate: true,
  message: true,
  observation: true,
  multiple: true,
  amountOfParcel: true,
  serial: true,
  fractionOfParcel: true,
  invoiceNumber: true,
  invoiceIssueDate: true,
  removePunctualDiscount: true,
  resetExtendedExpiration: true,
});

/** Percentual aceito pelos campos de desconto e multa. */
const percentage = z
  .number()
  .min(0, 'parcelCreate.validation.percentageRange')
  .max(100, 'parcelCreate.validation.percentageRange');

/**
 * Schema base dos formulários de parcela.
 *
 * O schema gerado é permissivo — o OpenAPI marca tudo como opcional —, então os
 * cinco campos que a tela legada marca com asterisco são endurecidos aqui. As
 * mensagens são **chaves de i18n**, não texto final: o schema é derivado do gerado
 * e não tem acesso ao `t()`.
 *
 * As duas datas são sobrescritas de `datetime()` para string livre porque os
 * campos guardam a data ISO do `DatePicker` (`YYYY-MM-DD`); a conversão para
 * data-hora acontece nos builders de payload, na fronteira do envio.
 */
export const parcelFormSchema = editableShape.extend({
  // Obrigatório na tela legada, mas hoje inalcançável: não existe endpoint que
  // liste os tipos de parcela. A mensagem explica o bloqueio em vez de acusar o
  // operador de não ter preenchido.
  typeId: z.number({ invalid_type_error: 'parcelCreate.validation.typeRequired' }).int(),
  numberParcel: z
    .number({ invalid_type_error: 'parcelCreate.validation.required' })
    .int('parcelCreate.validation.numberParcelRange')
    .min(1, 'parcelCreate.validation.numberParcelRange'),
  total: z.number({ invalid_type_error: 'parcelCreate.validation.required' }),
  paymentMethod: z.number({ invalid_type_error: 'parcelCreate.validation.required' }).int(),
  expirationDate: z
    .string({ invalid_type_error: 'parcelCreate.validation.expirationDateRequired' })
    .min(1, 'parcelCreate.validation.expirationDateRequired'),
  invoiceIssueDate: z.string().nullish(),
  discountPunctualPercentage: percentage.nullish(),
  retroactiveDiscount: percentage.optional(),
  totalDiscount: percentage.optional(),
  fine: percentage.nullish(),
});

/**
 * Valores compartilhados pelos formulários de criação e de edição de parcela.
 *
 * É o tipo que os organisms de seção recebem — por isso é único para as duas
 * telas.
 */
export type ParcelFormValues = z.infer<typeof parcelFormSchema>;
